import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { http } from "@/lib/api";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

export default function CheckoutSuccess() {
  const [search] = useSearchParams();
  const sessionId = search.get("session_id");
  const [state, setState] = useState({ status: "polling", payment_status: "pending", order_number: null });

  const poll = useCallback(async (attempts = 0) => {
    if (!sessionId) return;
    if (attempts >= 8) {
      setState(s => ({ ...s, status: "timeout" }));
      return;
    }
    try {
      const { data } = await http.get(`/checkout/status/${sessionId}`);
      setState(data);
      if (data.payment_status === "paid") return;
      if (data.status === "expired") return;
      setTimeout(() => poll(attempts + 1), 2000);
    } catch (e) {
      setTimeout(() => poll(attempts + 1), 2500);
    }
  }, [sessionId]);

  useEffect(() => { poll(); }, [poll]);

  const paid = state.payment_status === "paid";
  const expired = state.status === "expired";

  return (
    <Layout>
      <section className="bg-black min-h-[70vh] py-24">
        <div className="max-w-[800px] mx-auto px-6 md:px-12 text-center">
          {paid ? (
            <>
              <CheckCircle2 size={64} className="mx-auto text-[#B8915A] mb-6" />
              <h1 className="text-grunge text-5xl md:text-6xl text-[#F5F2EB] mb-4">THANK YOU.</h1>
              <p className="font-body italic text-xl text-[#D4B07B] mb-8">Your truth is on its way.</p>
              {state.order_number && (
                <p className="font-ui text-sm tracking-[0.22em] uppercase text-[#A3A3A3] mb-8">
                  Order <span className="text-[#F5F2EB]" data-testid="success-order-number">{state.order_number}</span>
                </p>
              )}
              <div className="flex flex-wrap gap-3 justify-center">
                {state.order_number && (
                  <Link to={`/proof/${state.order_number}`} className="btn-outline">View Proof of Purchase</Link>
                )}
                <Link to="/shop" className="btn-copper">Discover More</Link>
              </div>
            </>
          ) : expired ? (
            <>
              <AlertCircle size={64} className="mx-auto text-red-400 mb-6" />
              <h1 className="text-grunge text-4xl text-[#F5F2EB] mb-4">SESSION EXPIRED</h1>
              <p className="font-body text-[#A3A3A3] mb-6">Your payment session timed out. Your cart was cleared — please try again.</p>
              <Link to="/shop" className="btn-copper">Back to the Shop</Link>
            </>
          ) : (
            <>
              <Loader2 size={48} className="mx-auto text-[#B8915A] mb-6 animate-spin" />
              <h1 className="text-grunge text-3xl text-[#F5F2EB] mb-2">Confirming your payment…</h1>
              <p className="font-body text-[#A3A3A3]">Hold tight. We’re finalizing the details.</p>
            </>
          )}
        </div>
      </section>
    </Layout>
  );
}
